// Shared sorting utilities for consistent behavior across the app

/**
 * Check if a string starts with an emoji
 */
export function startsWithEmoji(text: string): boolean {
  if (!text) return false
  const regexEmoji = /^\p{Extended_Pictographic}/u
  return regexEmoji.test(text)
}

/**
 * Check if a string starts with "@" (special folders)
 */
export function startsWithAtSymbol(text: string): boolean {
  if (!text) return false
  return text.startsWith('@')
}

/**
 * Generic sorting function for items with title/name properties
 * Follows the same logic as the native app:
 * 1. Folders before notes (if applicable)
 * 2. Non-emoji items before emoji items
 * 3. Natural alphabetical sorting within each group
 */
export function sortItemsByTitle<T>(
  a: T,
  b: T,
  options: {
    getTitleFn: (item: T) => string
    getIsFolderFn?: (item: T) => boolean
  }
): number {
  const { getTitleFn, getIsFolderFn } = options

  // Sort folders before notes if folder detection is available
  if (getIsFolderFn) {
    const aIsFolder = getIsFolderFn(a)
    const bIsFolder = getIsFolderFn(b)
    if (aIsFolder && !bIsFolder) {
      return -1
    } else if (!aIsFolder && bIsFolder) {
      return 1
    }
  }

  // Sort emojis and @ folders down, like in the native app
  const aTitle = getTitleFn(a)
  const bTitle = getTitleFn(b)
  const aStartsWithEmoji = startsWithEmoji(aTitle)
  const bStartsWithEmoji = startsWithEmoji(bTitle)
  const aStartsWithAt = startsWithAtSymbol(aTitle)
  const bStartsWithAt = startsWithAtSymbol(bTitle)
  
  // Check if either item should be sorted down (emoji or @)
  const aSortDown = aStartsWithEmoji || aStartsWithAt
  const bSortDown = bStartsWithEmoji || bStartsWithAt
  
  if (aSortDown && !bSortDown) {
    return 1
  } else if (!aSortDown && bSortDown) {
    return -1
  }

  // Natural sort for numeric parts
  return aTitle.localeCompare(bTitle, undefined, { numeric: true })
}

/**
 * Convenience function for sorting Note objects (sidebar use case)
 */
export function sortNotes<T extends { title?: string; isFolder?: boolean }>(
  a: T,
  b: T
): number {
  return sortItemsByTitle(a, b, {
    getTitleFn: (item) => item.title ?? '',
    getIsFolderFn: (item) => item.isFolder ?? false,
  })
}

/**
 * Convenience function for sorting folder objects (filter dropdown use case)
 */
export function sortFolders<T extends { name: string }>(a: T, b: T): number {
  return sortItemsByTitle(a, b, {
    getTitleFn: (item) => item.name,
    // No folder detection needed since all items are folders
  })
}

/**
 * Convenience function for sorting folder objects by filename/path (filter dropdown use case)
 * This ensures parent folders appear before their subfolders
 */
export function sortFoldersByFilename<T extends { filename: string }>(a: T, b: T): number {
  return sortItemsByTitle(a, b, {
    getTitleFn: (item) => item.filename,
    // No folder detection needed since all items are folders
  })
}

/**
 * Convenience function for sorting folder objects by full path including teamspace names
 * This ensures proper hierarchical sorting across both private and teamspace folders
 */
export function sortFoldersByFullPath<T extends { fullPath: string }>(a: T, b: T): number {
  return sortItemsByTitle(a, b, {
    getTitleFn: (item) => item.fullPath,
    // No folder detection needed since all items are folders
  })
} 